import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import InformacionDocumento from './InformacionDocumento'
import ConfiguracionProgramacion from './ConfiguracionProgramacion'
import { FacturaAPI } from '../../../../../../../../api/facturas.api'
import { buildPatchScheduledFacturaPayload } from './utils/patchPayload'
import { sileo } from 'sileo'

export default function EditarProgramado({ doc, onBack, isModal = false }) {
  const api = new FacturaAPI()
  // Initialization matching NuevoProgramado structure + doc data
  const [formState, setFormState] = useState({
    client: {
      id: doc.receiver,
      tax_id: doc.receiver_tax_id || doc.receiver_data?.tax_id,
      rut: doc.receiver_tax_id || doc.receiver_data?.tax_id,
      razon_social: doc.receiver_name || doc.receiver_data?.name,
      direccion: doc.receiver_address || doc.receiver_data?.address,
      ciudad: doc.receiver_city || doc.receiver_data?.city,
      giro: doc.economic_activity || doc.receiver_data?.economic_activity || 'N/A',
      codigo_actividad: doc.customer_activity_code,
      contacto: doc.receiver_contact || doc.receiver_data?.contact_name,
      addresses: doc.receiver_data?.addresses || [],
      activities: doc.receiver_data?.activities || [],
      isAddressManual: false,
      isActivityManual: false
    },
    documentType: String(doc.dte_type_data?.code || '33'),
    currency: doc.currency || 'CLP',
    amount: doc.amount || 0,
    details: (doc.details || []).map(d => ({
      ...d,
      id: d.id || Date.now() + Math.random(),
      quantity: Number(d.quantity) || 0,
      unit_price: Number(d.unit_price) || 0,
      item_name: d.item_name || '',
      item_description: d.item_description || '',
      showDescription: !!d.item_description,
      item_code: d.item_code || '',
      item_type_code: d.item_type_code || ''
    })),
    references: (doc.references || []).map(r => ({
      ...r,
      id: r.id || Date.now() + Math.random()
    })),
    showReferencesBox: (doc.references || []).length > 0,
    config: {
      frequency: doc.frequency || 'monthly',
      start_date: doc.start_date || new Date().toISOString().split('T')[0],
      day_of_month: doc.day_of_month || 1,
      day_of_week: (() => {
        const val = doc.day_of_week
        if (typeof val === 'number') {
          const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday'
          ]
          return days[val - 1] || 'monday'
        }
        return String(val || 'monday').toLowerCase()
      })(),
      emission_day_adjustment: doc.emission_day_adjustment || 'none',
      end_type: doc.end_type || 'never',
      end_date: doc.end_date || '',
      max_occurrences: doc.max_occurrences || null,
      currency_day: doc.currency_day || 1
    }
  })

  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    // Validation
    const newErrors = {}

    if (!formState.client?.id) {
      newErrors.client = 'Selecciona un cliente válido para continuar'
    }

    const details = formState.details || []
    if (details.length === 0) {
      newErrors.details = 'Debes agregar al menos una línea de detalle'
    } else {
      const hasEmptyName = details.some(d => !String(d.item_name || '').trim())
      if (hasEmptyName) newErrors.details = 'Cada detalle debe tener nombre de producto'
    }

    const endType = formState.config.end_type
    if (endType === 'on_date' && !formState.config.end_date) {
      newErrors.end_date = 'Debes seleccionar una fecha fin'
    }
    if (endType === 'after_occurrences') {
      const occ = Number(formState.config.max_occurrences)
      if (!Number.isFinite(occ) || occ < 1) {
        newErrors.max_occurrences = 'Debes indicar un número de emisiones mayor o igual a 1'
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Calculate total amount from details
    const totalAmount = details.reduce(
      (acc, d) => acc + (Number(d.quantity) || 0) * (Number(d.unit_price) || 0),
      0
    )

    // Build complete updated payload
    const updatedFormState = {
      ...formState,
      amount: totalAmount
    }
    const payload = buildPatchScheduledFacturaPayload(updatedFormState, doc.status)

    setSaving(true)
    try {
      // Using the required endpoint: updateDocumentosProgramados
      await api.updateDocumentosProgramados(doc.id, doc.sender, payload)
      sileo.success({
        title: 'Actualización Exitosa',
        description: (
          <span className='text-green-500/80 font-medium'>
            Programación actualizada exitosamente... 👌
          </span>
        )
      })
      onBack()
    } catch (error) {
      console.error('Error updating schedule:', error)
      sileo.error({
        title: 'Error al Actualizar',
        description: (
          <span className='text-red-500/80 font-medium'>
            {'Error al guardar los cambios 🤯' + JSON.stringify(error.response.data.message)}
          </span>
        )
      })
    } finally {
      setSaving(false)
    }
  }

  const clearError = field => {
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors[field]
      return newErrors
    })
  }

  // const handlePreviewPDF = () => {
  //   // Basic preview logic placeholder
  //   sileo.info({
  //     title: 'Previsualización PDF',
  //     description: 'Previsualización de PDF generada (provisional) 📄'
  //   })
  // }

  return (
    <div className='animate-in fade-in duration-500'>
      <div className='flex items-center justify-end'>
        {!isModal && (
          <button onClick={onBack} className='mr-auto flex items-center gap-2 text-gray-600'>
            <div className='p-1.5 rounded-full bg-gray-100'>
              <ArrowLeft size={18} />
            </div>
            <span className='font-medium'>Volver</span>
          </button>
        )}

        {/* <button
          onClick={handlePreviewPDF}
          disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 text-indigo-600 rounded-lg text-sm font-bold transition-all shadow-sm active:scale-95 ${
            saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-100'
          }`}
        >
          <FileDown size={18} />
          Previsualizar PDF
        </button> */}
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        <div className='lg:col-span-2'>
          <InformacionDocumento
            formState={formState}
            setFormState={setFormState}
            errors={errors}
            clearError={clearError}
            setMasterEntityId={() => { }}
          />
        </div>

        <div className='lg:col-span-1'>
          <ConfiguracionProgramacion
            formState={formState}
            setFormState={setFormState}
            handleSave={handleSave}
            saving={saving}
          />
        </div>
      </div>
      <style>{`
          .section-title {
             display: flex;
             align-items: center;
             gap: 0.75rem;
             font-size: 1.1rem;
             font-weight: 600;
             color: #1e293b;
             margin-bottom: 1.5rem;
             padding-bottom: 0.75rem;
             border-bottom: 1px solid #f1f5f9;
          }

          .section-number {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            font-size: 0.85rem;
            color: #64748b;
          }
        `}</style>
    </div>
  )
}
